import { memo } from "react";
import type { KeyboardEvent } from "react";
import { BOARD, GO_SALARY, GROUP_INFO } from "@grundbesitz/shared";
import type { ColorGroup, Field, PropertyState } from "@grundbesitz/shared";
import { buildingSlots, compactName, fieldGeometry, fitName, jailLayout, pickInk, rectCenter } from "../../lib/boardLayout";
import type { FieldGeometry, FittedName, Rect } from "../../lib/boardLayout";
import { formatMoney } from "../../lib/format";
import { HotelIcon, HouseIcon } from "./BuildingIcons";
import { FieldGlyph, glyphFor } from "./FieldGlyph";
import type { GlyphKind } from "./FieldGlyph";
import { GROUP_CODE } from "./GroupBadge";

export interface OwnerMark {
  hex: string;
  initial: string;
}

export interface FieldCellProps {
  index: number;
  state: PropertyState | undefined;
  owner: OwnerMark | null;
  selected: boolean;
  label: string;
  /** Prefix of the `<pattern>` ids defined by the board. */
  uid: string;
  onClick: (index: number) => void;
}

const PAD = 6;
const GLYPH_COLUMN = 44;
const INK = "#1b1a17";

export const patternId = (uid: string, group: ColorGroup) => `gb-${uid}-pat-${group}`;
export const hatchId = (uid: string) => `gb-${uid}-hatch`;

function inset(r: Rect, by: number): Rect {
  return { x: r.x + by, y: r.y + by, w: r.w - 2 * by, h: r.h - 2 * by };
}

const box = (r: Rect) => ({ x: r.x, y: r.y, width: r.w, height: r.h });

/** Labels shorter than the field names, so the two card fields stay legible on a small cell. */
function shortName(field: Field): string {
  if (field.kind === "CHANCE") return "Ereignis";
  if (field.kind === "COMMUNITY") return "Gemeinschaft";
  return field.name;
}

function setLines({ lines, fontSize }: FittedName, content: Rect) {
  const cx = content.x + content.w / 2;
  return lines.map((line, i) => (
    <tspan key={i} x={cx} y={content.y + PAD + fontSize * 0.92 + i * fontSize * 1.1}>
      {line}
    </tspan>
  ));
}

/** Name in two forms: the full one, and a shorter, larger one that a small board switches to (see board.css). */
function NameLines({ name, content, maxFont = 17 }: { name: string; content: Rect; maxFont?: number }) {
  const width = content.w - 2 * PAD;
  const full = fitName(name, width, maxFont);
  const compact = compactName(name, width);
  return (
    <>
      <text className="gb-name gb-name-full" fontSize={full.fontSize}>
        {setLines(full, content)}
      </text>
      <text className="gb-name gb-name-compact" fontSize={compact.fontSize}>
        {setLines(compact, content)}
      </text>
    </>
  );
}

function BarContent({ geometry, state, group }: { geometry: FieldGeometry; state: PropertyState | undefined; group: ColorGroup }) {
  const bar = geometry.bar!;
  const count = state?.buildings ?? 0;
  const c = rectCenter(bar);
  if (count > 0) {
    const hotel = count >= 5;
    const slots = buildingSlots(bar, hotel ? 1 : count);
    const first = slots[0];
    const along = geometry.portrait;
    const span = slots.length * first.size + 8;
    const plate: Rect = along
      ? { x: c.x - span / 2, y: bar.y + 3, w: span, h: bar.h - 6 }
      : { x: bar.x + 3, y: c.y - span / 2, w: bar.w - 6, h: span };
    return (
      <g aria-hidden="true">
        <rect {...box(plate)} rx="6" fill="#fffdf8" fillOpacity="0.94" stroke={INK} strokeOpacity="0.4" strokeWidth="1.2" />
        {slots.map((s, i) => (
          <g key={i} transform={`translate(${s.x - s.size / 2} ${s.y - s.size / 2})`}>
            {hotel ? <HotelIcon size={s.size} /> : <HouseIcon size={s.size} />}
          </g>
        ))}
      </g>
    );
  }
  const color = GROUP_INFO[group].color;
  return (
    <text className="gb-bar-code" x={c.x} y={c.y + 5} fill={pickInk(color)} stroke={color} aria-hidden="true">
      {GROUP_CODE[group]}
    </text>
  );
}

function Glyph({ kind, content, top, bottom }: { kind: GlyphKind; content: Rect; top: number; bottom: number }) {
  const bandTop = content.y + top;
  const bandHeight = content.h - top - bottom;
  const size = Math.max(22, Math.min(44, bandHeight, content.w - 14));
  return <FieldGlyph kind={kind} size={size} x={content.x + content.w / 2 - size / 2} y={bandTop + bandHeight / 2 - size / 2} />;
}

function OrdinaryBody({ field, geometry }: { field: Exclude<Field, { kind: "GO" | "JAIL" | "FREE_PARKING" | "GO_TO_JAIL" }>; geometry: FieldGeometry }) {
  const glyph = glyphFor(field);
  // Wide cells (left and right column) put the symbol beside the text, tall cells stack it between name and price.
  const beside = glyph !== null && !geometry.portrait;
  const content: Rect = beside ? { ...geometry.content, x: geometry.content.x + GLYPH_COLUMN, w: geometry.content.w - GLYPH_COLUMN } : geometry.content;
  const cx = content.x + content.w / 2;
  const nameFont = field.kind === "CHANCE" || field.kind === "COMMUNITY" ? 15 : 17;
  const fitted = fitName(shortName(field), content.w - 2 * PAD, nameFont);
  const nameBottom = PAD + fitted.fontSize * (0.92 + 1.1 * (fitted.lines.length - 1)) + 4;
  const hasPrice = field.kind === "STREET" || field.kind === "RAILROAD" || field.kind === "UTILITY";
  const whole = geometry.content;
  return (
    <>
      <NameLines name={shortName(field)} content={content} maxFont={nameFont} />
      {glyph && beside ? (
        <FieldGlyph kind={glyph} size={40} x={whole.x + 3} y={whole.y + whole.h / 2 - 20} />
      ) : glyph ? (
        <Glyph kind={glyph} content={content} top={nameBottom} bottom={hasPrice || field.kind === "TAX" ? 24 : 6} />
      ) : null}
      {hasPrice ? (
        <text className="gb-price" x={cx} y={content.y + content.h - 8}>
          {formatMoney(field.price)}
        </text>
      ) : null}
      {field.kind === "TAX" ? (
        <text className="gb-amount" x={cx} y={content.y + content.h - 8}>
          {formatMoney(field.amount)}
        </text>
      ) : null}
    </>
  );
}

function CornerBody({ field, geometry }: { field: Extract<Field, { kind: "GO" | "JAIL" | "FREE_PARKING" | "GO_TO_JAIL" }>; geometry: FieldGeometry }) {
  const { x, y, w, h } = geometry.rect;
  const cx = x + w / 2;
  switch (field.kind) {
    case "GO":
      return (
        <>
          <text className="gb-corner-title" x={cx} y={y + 50} fontSize="42">
            LOS
          </text>
          <FieldGlyph kind="go" size={62} x={cx - 31} y={y + 62} />
          <text className="gb-small" x={cx} y={y + h - 8} fontSize="15">
            +{formatMoney(GO_SALARY)}
          </text>
        </>
      );
    case "FREE_PARKING":
      return (
        <>
          <text className="gb-corner-title" fontSize="24">
            <tspan x={cx} y={y + 34}>
              Frei
            </tspan>
            <tspan x={cx} y={y + 60}>
              Parken
            </tspan>
          </text>
          <FieldGlyph kind="parking" size={64} x={cx - 32} y={y + h - 76} />
        </>
      );
    case "GO_TO_JAIL":
      return (
        <>
          <text className="gb-corner-title" fontSize="19">
            <tspan x={cx} y={y + 28}>
              Gehen Sie
            </tspan>
            <tspan x={cx} y={y + 49}>
              in das
            </tspan>
            <tspan x={cx} y={y + 70}>
              Gefängnis
            </tspan>
          </text>
          <FieldGlyph kind="gotojail" size={56} x={cx - 28} y={y + h - 68} />
        </>
      );
    case "JAIL": {
      const { cell } = jailLayout(0, 0);
      return (
        <>
          <rect className="gb-jail-cell" {...box(cell)} rx="6" />
          <text className="gb-corner-title" x={cell.x + cell.w / 2} y={cell.y + 17} fontSize="14">
            Gefängnis
          </text>
          <text className="gb-small" x={x + 6 + (w - 12) / 2} y={cell.y + cell.h + 10} fontSize="11.5">
            Nur zu Besuch
          </text>
        </>
      );
    }
  }
}

export const FieldCell = memo(function FieldCell({ index, state, owner, selected, label, uid, onClick }: FieldCellProps) {
  const field = BOARD[index];
  const geometry = fieldGeometry(index);
  const { rect, bar, stripe, content } = geometry;
  const mortgaged = !!state?.mortgaged;
  const isCorner = geometry.side === "corner";

  const onKeyDown = (e: KeyboardEvent<SVGGElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(index);
    }
  };

  let mark: Rect;
  if (bar) mark = bar;
  else mark = { x: content.x + content.w / 2 - 15, y: content.y + content.h / 2 - 15, w: 30, h: 30 };
  const markCenter = rectCenter(mark);

  return (
    <g className="gb-field" role="button" tabIndex={0} aria-label={label} aria-current={selected ? "true" : undefined} onClick={() => onClick(index)} onKeyDown={onKeyDown}>
      <rect className={field.kind === "GO" ? "gb-cell gb-corner-go" : "gb-cell"} {...box(rect)} />
      {field.kind === "STREET" && bar ? <rect {...box(bar)} fill={`url(#${patternId(uid, field.group)})`} stroke="var(--board-line)" strokeWidth="2" /> : null}
      {mortgaged ? (
        <g pointerEvents="none">
          <rect className="gb-mortgage-wash" {...box(rect)} />
          <rect {...box(rect)} fill={`url(#${hatchId(uid)})`} />
        </g>
      ) : null}
      <g opacity={mortgaged ? 0.6 : 1}>
        {field.kind === "STREET" && !mortgaged ? <BarContent geometry={geometry} state={state} group={field.group} /> : null}
        {isCorner ? (
          <CornerBody field={field as Extract<Field, { kind: "GO" | "JAIL" | "FREE_PARKING" | "GO_TO_JAIL" }>} geometry={geometry} />
        ) : (
          <OrdinaryBody field={field as Exclude<Field, { kind: "GO" | "JAIL" | "FREE_PARKING" | "GO_TO_JAIL" }>} geometry={geometry} />
        )}
      </g>
      {owner && stripe ? (
        <g aria-hidden="true">
          <rect {...box(stripe)} fill={owner.hex} stroke={INK} strokeOpacity="0.55" strokeWidth="1.5" />
          <text className="gb-stripe-letter" x={stripe.x + stripe.w / 2} y={stripe.y + stripe.h / 2 + 4.6} fill={pickInk(owner.hex)}>
            {owner.initial}
          </text>
        </g>
      ) : null}
      {mortgaged ? (
        <g aria-hidden="true" pointerEvents="none">
          <circle className="gb-mortgage-mark" cx={markCenter.x} cy={markCenter.y} r={bar ? 12.5 : 15} />
          <text className="gb-mortgage-letter" x={markCenter.x} y={markCenter.y + 6}>
            H
          </text>
        </g>
      ) : null}
      <rect className="gb-hover" {...box(rect)} />
      {selected ? (
        <>
          <rect className="gb-selected-outer" {...box(inset(rect, 4))} />
          <rect className="gb-selected-inner" {...box(inset(rect, 9))} />
        </>
      ) : null}
      <rect className="gb-ring" {...box(inset(rect, 4))} />
    </g>
  );
});
